import { Box, FormHelperText, Stack, Typography } from "@mui/material";

/**
 * Wraps a form input with a consistent label, helper text, and error block.
 *
 * Use as the parent of any TextField, Select, custom widget, etc. — pass
 * the input as children. The wrapping <TextField label="…"> floating-label
 * pattern works for simple inputs, but breaks down for selects, switches,
 * custom widgets, and anything that wants a description above the field.
 * This component normalizes all of them.
 *
 *   <FormField label="Vendor name" required error={errors.name} helper="Shown on receipts.">
 *     <TextField value={…} onChange={…} fullWidth />
 *   </FormField>
 */
export default function FormField({
  label,
  required = false,
  helper,
  error,
  children,
  sx,
}) {
  const hasError = Boolean(error);
  return (
    <Box sx={sx}>
      {label && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.75 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color={hasError ? "error.main" : "text.primary"}
          >
            {label}
          </Typography>
          {required && (
            <Typography variant="body2" color="error.main" component="span">
              *
            </Typography>
          )}
        </Stack>
      )}
      {children}
      {(helper || hasError) && (
        <FormHelperText error={hasError} sx={{ mx: 0, mt: 0.75 }}>
          {hasError ? error : helper}
        </FormHelperText>
      )}
    </Box>
  );
}
